import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'
import { newId, format, capitalize, floatify } from '@/utils'
import { DateTime } from 'luxon'

const getDefaultState = () => {
  return {
    data: [],
    ref: ''
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return { ...state.data }
  },
  ref(state) {
    return state.ref
  },
  inventories(state) {
    return Object.assign({}, ...state.data.map(i => ({ [i.id]: i })))
  },
  inventoriesOrdered(state, getters) {
    return [
      ...Object.values(getters['inventories']).sort(
        (a, b) => b.date.seconds - a.date.seconds
      )
    ]
  },
  inventoryById(state) {
    return id => {
      try {
        return state.data.find(doc => doc.id === id)
      } catch {
        return null
      }
    }
  },
  isInventory(state, getters) {
    return id => {
      try {
        return Object.keys(getters.data).some(k => getters.data[k].id === id)
      } catch {
        return false
      }
    }
  },
  inventoryCsvByInventoryId(state, getters, rootState, rootGetters) {
    return inventoryId => {
      console.time()

      if (!getters['inventories'][inventoryId])
        throw new Error(
          'Invalid InventoryId: ',
          inventoryId ? inventoryId : 'No ID Provided'
        )

      let categoryTotals = {}
      let inventoryTotal = 0
      let rows = [
        [
          'Product',
          'Expense Category',
          'Vendor',
          'Code',
          'Order Unit',
          'Counting Unit',
          'Each',
          'Units-Per-Order',
          'Price',
          'Count',
          'Value'
        ]
      ]

      let categories = {
        food: [
          '5210 Baked Goods',
          '5220 Beverages',
          '5232 Pantry',
          '5233 Fruits and Vegetables',
          '5241 Beef',
          '5242 Chicken',
          '5243 Eggs',
          '5244 Pork',
          '5245 Tofu'
        ]
      }

      let getInventoryCategory = (query, list = categories) => {
        for (const key in list) {
          if (categories[key].includes(query)) return key
        }
        return 'other'
      }
      let formatCsv = amount => format(amount).replace(',', '')
      let makeTotalsRow = (name, amount, width) => {
        return [
          `Total ${capitalize(name)} Value`,
          ...Array(width - 2).fill(''),
          `${formatCsv(amount)}`
        ]
      }

      Object.entries(getters['inventories'][inventoryId])
        .filter(
          entry => !!rootGetters['storeModule/products/products'][entry[0]]
        )
        .map(
          // sort by dailyOrdering order
          entry => ({
            ...entry[1],
            id: entry[0], // explicitly set product id from the entry key
            orderIndex: rootGetters['storeModule/dailyOrdering'].findIndex(
              a => a === entry[0]
            )
          })
        )
        .sort((a, b) => {
          return a.orderIndex - b.orderIndex
        })
        .forEach(item => {
          let product = rootGetters['storeModule/products/products'][item.id]
          let category = getInventoryCategory(product.category)
          let vendorName =
            rootGetters['storeModule/vendors/vendors'][product.vendor] &&
            rootGetters['storeModule/vendors/vendors'][product.vendor].name

          let price = item.price || 0
          let amountEntered = item.amount || 0
          let qtyPerUnit = product.qtyPerUnit || 1
          let each = floatify(price / qtyPerUnit || 0)
          let value = floatify(each * amountEntered || 0)

          if (value && !isNaN(value)) {
            categoryTotals[category] =
              Number(categoryTotals[category] || 0) + Number(value)
            inventoryTotal += Number(value)
          }

          rows.push([
            `${product.name || ''}`,
            `${category || ''}`,
            `${vendorName || ''}`,
            `${product.code || ''}`,
            `${
              product.unit
                ? product.unit
                    .replace(/(\r\n|\n|\r)/gm, '') // delete new lines
                    .replace(/(\s\s)/gm, ' ') // replace multiple spaces with single space
                    .replace(/"/g, '""') // replace single quote with double quote
                : ''
            }`,
            `${product.unitCounting || ''}`,
            `${formatCsv(each) || '0'}`,
            `${qtyPerUnit || ''}`,
            `${formatCsv(price) || '0'}`,
            `${amountEntered || '0'}`,
            `${formatCsv(value) || '0'}`
          ])
          // }
        })

      rows.push(Array(rows.length).fill(''))

      Object.keys(categoryTotals).forEach(key => {
        rows.push([...makeTotalsRow(key, categoryTotals[key]), rows[0].length])
      })
      rows.push([...makeTotalsRow('inventory', inventoryTotal, rows[0].length)])

      let convertRowToCSV = row => '"' + row.join('","') + '"' + '\n'
      let convertArrayToCSV = array =>
        array.reduce((output, row) => output + convertRowToCSV(row), '')

      let outputCsv = convertArrayToCSV(rows)

      // create filename "20200115_inventory_Noma.csv"
      let file = {
        date:
          DateTime.fromSeconds(getters['inventories'][inventoryId].date.seconds)
            .setZone(rootGetters['storemodule/timezone'])
            .toISODate({
              format: 'basic'
            }) || 'invalidDate',
        type: 'inventory',
        store: rootGetters['storeModule/name']
      }

      let filename = `${file.date}_${file.type}_${file.store}.csv`

      console.timeEnd()
      return { filename, data: outputCsv }
    }
  },
  detailedInventoryCsvByInventoryId(state, getters, rootState, rootGetters) {
    return inventoryId => {
      console.time('detailedInventoryCsv')

      if (!getters['inventories'][inventoryId])
        throw new Error(
          'Invalid InventoryId: ',
          inventoryId ? inventoryId : 'No ID Provided'
        )

      let categoryTotals = {}
      let inventoryTotal = 0
      let rows = [
        [
          // Product Information
          'Product Name',
          'Product ID',
          'Invoice Name',
          'Invoice Code',
          'Generic Product ID',
          'Expense Category',
          'Location',
          'Par Amount',
          'Active',
          'Transferable',
          // Vendor Information
          'Vendor Name',
          'Vendor ID',
          'Vendor Contact',
          'Vendor Phone',
          'Vendor Email',
          'Vendor Address',
          'Vendor Account',
          'Vendor Terms',
          'Vendor Expense Account',
          'Minimum Order',
          'Minimum Cases',
          // Unit/Measurement Information
          'Order Unit',
          'Counting Unit',
          'Recipe Unit',
          'Qty Per Unit',
          'Recipe Unit Per Order Unit',
          'Yield Percent',
          // Inventory Data
          'Count',
          'Price',
          'Each',
          'Value',
          'Units Used',
          // Instructions/Questions
          'Question',
          'Counting Instructions',
          'Inventory Question',
          'Input Type'
        ]
      ]

      let categories = {
        food: [
          '5210 Baked Goods',
          '5220 Beverages',
          '5232 Pantry',
          '5233 Fruits and Vegetables',
          '5241 Beef',
          '5242 Chicken',
          '5243 Eggs',
          '5244 Pork',
          '5245 Tofu'
        ]
      }

      let getInventoryCategory = (query, list = categories) => {
        for (const key in list) {
          if (categories[key].includes(query)) return key
        }
        return 'other'
      }
      let formatCsv = amount => format(amount).replace(',', '')
      let makeTotalsRow = (name, amount, width) => {
        return [
          `Total ${capitalize(name)} Value`,
          ...Array(width - 2).fill(''),
          `${formatCsv(amount)}`
        ]
      }

      Object.entries(getters['inventories'][inventoryId])
        .filter(
          entry => !!rootGetters['storeModule/products/products'][entry[0]]
        )
        .map(
          // sort by dailyOrdering order
          entry => ({
            ...entry[1],
            id: entry[0], // explicitly set product id from the entry key
            orderIndex: rootGetters['storeModule/dailyOrdering'].findIndex(
              a => a === entry[0]
            )
          })
        )
        .sort((a, b) => {
          return a.orderIndex - b.orderIndex
        })
        .forEach(item => {
          let product = rootGetters['storeModule/products/products'][item.id]
          let category = getInventoryCategory(product.category)
          let vendor =
            rootGetters['storeModule/vendors/vendors'][product.vendor] || {}
          let vendorName = vendor.name || ''

          let price = item.price || 0
          let amountEntered = item.amount || 0
          let qtyPerUnit = product.qtyPerUnit || 1
          let each = floatify(price / qtyPerUnit || 0)
          let value = floatify(each * amountEntered || 0)

          if (value && !isNaN(value)) {
            categoryTotals[category] =
              Number(categoryTotals[category] || 0) + Number(value)
            inventoryTotal += Number(value)
          }

          rows.push([
            // Product Information
            `${product.name || ''}`,
            `${product.id || ''}`,
            `${product.details || ''}`,
            `${product.code || ''}`,
            `${product.genericProductId || ''}`,
            `${product.category || ''}`,
            `${product.location || ''}`,
            `${product.par || ''}`,
            `${product.active !== false ? 'Yes' : 'No'}`,
            `${product.transferable !== false ? 'Yes' : 'No'}`,
            // Vendor Information
            `${vendorName}`,
            `${product.vendor || ''}`,
            `${vendor.contact || ''}`,
            `${vendor.phone || ''}`,
            `${vendor.email || ''}`,
            `${vendor.address || ''}`,
            `${vendor.account || ''}`,
            `${vendor.terms || ''}`,
            `${vendor.expenseAccount || ''}`,
            `${vendor.minimumOrder || ''}`,
            `${vendor.minimumCases || ''}`,
            // Unit/Measurement Information
            `${
              product.unit
                ? product.unit
                    .replace(/(\r\n|\n|\r)/gm, '') // delete new lines
                    .replace(/(\s\s)/gm, ' ') // replace multiple spaces with single space
                    .replace(/"/g, '""') // replace single quote with double quote
                : ''
            }`,
            `${product.unitCounting || ''}`,
            `${product.unitRecipe || ''}`,
            `${qtyPerUnit || ''}`,
            `${product.recipeUnitPerOrderUnit || ''}`,
            `${product.yieldPercent || ''}`,
            // Inventory Data
            `${amountEntered || '0'}`,
            `${formatCsv(price) || '0'}`,
            `${formatCsv(each) || '0'}`,
            `${formatCsv(value) || '0'}`,
            `${item.unitsUsed || '0'}`,
            // Instructions/Questions
            `${
              product.question
                ? product.question
                    .replace(/(\r\n|\n|\r)/gm, ' ')
                    .replace(/"/g, '""')
                : ''
            }`,
            `${
              product.instructions
                ? product.instructions
                    .replace(/(\r\n|\n|\r)/gm, ' ')
                    .replace(/"/g, '""')
                : ''
            }`,
            `${
              product.inventoryQuestion
                ? product.inventoryQuestion
                    .replace(/(\r\n|\n|\r)/gm, ' ')
                    .replace(/"/g, '""')
                : ''
            }`,
            `${product.inputType || ''}`
          ])
        })

      rows.push(Array(rows[0].length).fill(''))

      Object.keys(categoryTotals).forEach(key => {
        rows.push([...makeTotalsRow(key, categoryTotals[key], rows[0].length)])
      })
      rows.push([...makeTotalsRow('inventory', inventoryTotal, rows[0].length)])

      let convertRowToCSV = row => '"' + row.join('","') + '"' + '\n'
      let convertArrayToCSV = array =>
        array.reduce((output, row) => output + convertRowToCSV(row), '')

      let outputCsv = convertArrayToCSV(rows)

      // create filename "20200115_inventory_detailed_Noma.csv"
      let file = {
        date:
          DateTime.fromSeconds(getters['inventories'][inventoryId].date.seconds)
            .setZone(rootGetters['storemodule/timezone'])
            .toISODate({
              format: 'basic'
            }) || 'invalidDate',
        type: 'inventory_detailed',
        store: rootGetters['storeModule/name']
      }

      let filename = `${file.date}_${file.type}_${file.store}.csv`

      console.timeEnd('detailedInventoryCsv')
      return { filename, data: outputCsv }
    }
  }
}

const actions = {
  bindStore: firestoreAction(
    ({ bindFirestoreRef, commit, state }, { storeId }) => {
      commit(
        'setRef',
        db
          .collection('stores')
          .doc(storeId)
          .collection('inventories')
      )
      return bindFirestoreRef(
        'data',
        state.ref
          .where(
            'date',
            '>=',
            DateTime.now()
              .minus({ days: 60 })
              .toJSDate()
          )
          .where('status', 'in', ['saved', 'open'])
      ) // .limit(10) base on date created
    }
  ),
  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),
  create: async function({ getters }, payload) {
    let id = newId()
    let data = {
      date: Timestamp.now(),
      createdAt: Timestamp.now(),
      status: 'open',
      completed: false,
      id,
      ...payload // date, completed, status
    }
    console.log('inventories/create', id, data)
    return await getters.ref
      .doc(id)
      .set(data, { merge: true })
      .then(() => id)
      .catch(err => err)
  },
  delete: async function({ dispatch }, id) {
    console.log('inventories/delete', id)
    if (!id) throw new Error('No ID provided.')
    return dispatch('updateItem', { id, data: { status: 'deleted' } })
  },
  updatePriceByInventoryIdByProductId(
    { dispatch },
    { inventoryId, id, price }
  ) {
    console.log(
      'invoice/updatePriceByInventoryIdByProductId',
      inventoryId,
      id,
      price
    )
    if (!inventoryId || !id || isNaN(price) || typeof price !== 'number')
      throw 'incorect data provided'

    let data = { [id + '.price']: price }

    return dispatch('updateItem', {
      id: inventoryId,
      data
    })
    // .then(() => {
    //   dispatch('fixTotalCostByInvoiceId', { invoiceId })
    // })
  },
  updateAmountEnteredByInventoryIdByProductId(
    { dispatch },
    { inventoryId, id, amount }
  ) {
    console.log(
      'inventories/updateAmountEnteredByInventoryIdByProductId',
      inventoryId,
      id,
      amount
    )
    if (!inventoryId || !id || isNaN(amount) || typeof amount !== 'number')
      throw 'incorect data provided'

    let data = { [id + '.amount']: amount }

    return dispatch('updateItem', {
      id: inventoryId,
      data
    })
    // .then(() => {
    //   dispatch('fixTotalCostByInvoiceId', { invoiceId })
    // })
  },
  updateItem({ getters }, { id, data }) {
    console.log('inventories/updateItem', id, data)
    return getters.ref.doc(id).update(data)
  },
  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const inventories = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions
}

export default inventories
